import React, { useMemo, useState } from 'react';
import { CheckCircle2, Flag, MessageSquare, XCircle } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import VisitFullReport from '../../components/shared/VisitFullReport';
import { getApiErrorMessage } from '../../lib/apiError';
import {
  formatWatDateTime,
  getLatestReview,
  getStatusClasses,
  getVisitDependantName,
  getVisitStart,
  normalizeVisitStatus,
  sortVisitsNewestFirst,
} from '../../lib/visitUtils';

const SupervisorReviewsPage = () => {
  const { visits, reviewVisit } = useApp();
  const [selectedVisitId, setSelectedVisitId] = useState(null);
  const [comment, setComment] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const reviewableVisits = useMemo(() => (
    sortVisitsNewestFirst(visits.filter((visit) => ['Submitted', 'Approved', 'Flagged', 'Rejected'].includes(normalizeVisitStatus(visit.status))))
  ), [visits]);
  const pending = reviewableVisits.filter((visit) => normalizeVisitStatus(visit.status) === 'Submitted');
  const completed = reviewableVisits.filter((visit) => normalizeVisitStatus(visit.status) !== 'Submitted');
  const selectedVisit = reviewableVisits.find((visit) => visit.id === selectedVisitId) || reviewableVisits[0] || null;
  const latestReview = getLatestReview(selectedVisit);
  const officerResponse = selectedVisit?.latestOfficerResponse;
  const selectedStatus = normalizeVisitStatus(selectedVisit?.status);
  const selectedFinalized = ['Approved', 'Rejected'].includes(selectedStatus);
  const selectedReviewable = ['Submitted', 'Flagged'].includes(selectedStatus);

  const handleReview = async (status) => {
    if (!selectedVisit) return;
    if ((status === 'Flagged' || status === 'Rejected') && !comment.trim()) {
      setError('Add a comment before flagging or rejecting.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const updated = await reviewVisit(selectedVisit.id, status, comment);
      setSelectedVisitId(updated.id);
      setComment('');
    } catch (reviewError) {
      setError(getApiErrorMessage(reviewError, 'Unable to save review.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F4EE]" data-testid="supervisor-reviews-page">
      <div className="border-b border-[#E8E6E3] bg-[#FAFAF7]">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:py-8">
          <h1 className="font-serif text-2xl font-semibold text-[#1A1A1A] sm:text-3xl">Reviews</h1>
          <p className="mt-1 text-sm text-[#5A5A5A]">Pending reports, completed review decisions, and care-officer flag responses.</p>
        </div>
      </div>

      <main className="mx-auto grid max-w-7xl gap-6 px-4 py-6 sm:px-6 lg:grid-cols-[420px_1fr] lg:py-8">
        <section className="space-y-5">
          <div className="rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7]">
            <div className="border-b border-[#E8E6E3] px-5 py-4">
              <h2 className="font-serif text-xl font-semibold text-[#1A1A1A]">Pending Review</h2>
              <p className="text-sm text-[#5A5A5A]">{pending.length} submitted visits</p>
            </div>
            <VisitList visits={pending} selectedVisitId={selectedVisit?.id} onSelect={setSelectedVisitId} />
          </div>

          <div className="rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7]">
            <div className="border-b border-[#E8E6E3] px-5 py-4">
              <h2 className="font-serif text-xl font-semibold text-[#1A1A1A]">Review History</h2>
              <p className="text-sm text-[#5A5A5A]">{completed.length} reviewed visits</p>
            </div>
            <VisitList visits={completed} selectedVisitId={selectedVisit?.id} onSelect={setSelectedVisitId} showResponseCue />
          </div>
        </section>

        <section className="rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7]">
          {selectedVisit ? (
            <div>
              <div className="border-b border-[#E8E6E3] p-5">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <h2 className="font-serif text-2xl font-semibold text-[#1A1A1A]">{getVisitDependantName(selectedVisit)}</h2>
                    <p className="mt-1 text-sm text-[#5A5A5A]">{formatWatDateTime(getVisitStart(selectedVisit))}</p>
                    <p className="mt-1 text-sm text-[#5A5A5A]">{selectedVisit.careOfficerName || selectedVisit.careOfficerSnapshot?.name || 'Care officer'}</p>
                  </div>
                  <span className={`w-fit rounded-full border px-3 py-1 text-sm font-medium ${getStatusClasses(selectedVisit.status)}`}>{normalizeVisitStatus(selectedVisit.status)}</span>
                </div>
              </div>

              <div className="max-h-[680px] space-y-5 overflow-y-auto p-5">
                <VisitFullReport visit={selectedVisit} />

                {latestReview && (
                  <div className="rounded-2xl border border-[#E8E6E3] p-4">
                    <p className="text-xs font-medium uppercase tracking-[0.12em] text-[#5A5A5A]">Latest decision</p>
                    <p className="mt-2 text-sm font-medium text-[#1A1A1A]">{latestReview.status}</p>
                    {latestReview.comments && <p className="mt-2 text-sm text-[#5A5A5A]">{latestReview.comments}</p>}
                  </div>
                )}

                {officerResponse && (
                  <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
                    <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.12em] text-amber-800">
                      <MessageSquare className="h-4 w-4" />
                      Care officer response
                    </p>
                    <p className="mt-2 text-sm text-amber-900">{officerResponse.comments}</p>
                    <p className="mt-2 text-xs text-amber-800">{officerResponse.respondedAt ? formatWatDateTime(officerResponse.respondedAt) : ''}</p>
                  </div>
                )}

                {selectedReviewable ? (
                  <>
                    <textarea
                      value={comment}
                      onChange={(event) => setComment(event.target.value)}
                      rows={4}
                      placeholder="Add supervisor comment..."
                      className="w-full rounded-2xl border border-[#D8D5D0] bg-[#FAFAF7] px-4 py-3 text-sm outline-none focus:border-[#9F1D35]"
                    />
                    {error && <p className="text-sm text-red-600">{error}</p>}
                    <div className="grid gap-3 sm:grid-cols-3">
                      <button
                        type="button"
                        disabled={saving}
                        onClick={() => handleReview('Approved')}
                        className="inline-flex items-center justify-center gap-2 rounded-full bg-emerald-600 px-4 py-3 text-sm font-medium text-white disabled:opacity-60"
                      >
                        <CheckCircle2 className="h-4 w-4" />
                        Approve
                      </button>
                      <button
                        type="button"
                        disabled={saving}
                        onClick={() => handleReview('Flagged')}
                        className="inline-flex items-center justify-center gap-2 rounded-full border border-amber-300 px-4 py-3 text-sm font-medium text-amber-800 disabled:opacity-60"
                      >
                        <Flag className="h-4 w-4" />
                        Remain Flagged
                      </button>
                      <button
                        type="button"
                        disabled={saving}
                        onClick={() => handleReview('Rejected')}
                        className="inline-flex items-center justify-center gap-2 rounded-full border border-red-300 px-4 py-3 text-sm font-medium text-red-700 disabled:opacity-60"
                      >
                        <XCircle className="h-4 w-4" />
                        Reject
                      </button>
                    </div>
                  </>
                ) : (
                  <p className="rounded-2xl border border-[#E8E6E3] bg-[#F7F4EE] p-4 text-sm text-[#5A5A5A]">
                    {selectedFinalized ? 'This visit is finalized. No further supervisor action can be taken.' : 'This visit is not ready for review.'}
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="p-10 text-center text-sm text-[#5A5A5A]">No visits are ready for review.</div>
          )}
        </section>
      </main>
    </div>
  );
};

const VisitList = ({ visits, selectedVisitId, onSelect, showResponseCue = false }) => (
  <div className="max-h-[420px] overflow-y-auto">
    {visits.map((visit) => (
      <button
        key={visit.id}
        type="button"
        onClick={() => onSelect(visit.id)}
        className={`block w-full border-b border-[#E8E6E3] p-4 text-left hover:bg-[#F5E8EA]/50 ${selectedVisitId === visit.id ? 'bg-[#F5E8EA]' : ''}`}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="font-medium text-[#1A1A1A]">{getVisitDependantName(visit)}</p>
            <p className="mt-1 text-xs text-[#5A5A5A]">{formatWatDateTime(getVisitStart(visit))}</p>
            {showResponseCue && normalizeVisitStatus(visit.status) === 'Flagged' && visit.latestOfficerResponse && (
              <span className="mt-2 inline-flex rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">View Response</span>
            )}
          </div>
          <span className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${getStatusClasses(visit.status)}`}>{normalizeVisitStatus(visit.status)}</span>
        </div>
      </button>
    ))}
    {visits.length === 0 && <div className="p-6 text-sm text-[#5A5A5A]">None yet.</div>}
  </div>
);

export default SupervisorReviewsPage;
